import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePage } from "@inertiajs/react";

import ContactModal from "/components/Projects/ContactModal";
import ContactButton from "/components/Projects/ContactButton";
import Button from "/components/Button";
import ProjectsFilter from "./Filter";
import ProjectCard from "./Card";
import ProjectSimpleCard from "./SimpleCard";
import { buildURLWithQueryString } from "/utils/helpers";
import { useHeaderHeight } from "/utils/header-height-context";

export default function ProjectsList({
  sourceURL,
}) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { t } = useTranslation();
  const {
    projects,
    total,
    per_page,
    project_type_options,
    client_options,
    layout_content_type_options,
    is_mobile,
    project_type_ids = [],
    client_ids = [],
    layout_content_type_ids = [],
  } = usePage().props;

  const headerHeight = useHeaderHeight();
  const [memTotal, setMemTotal] = useState(total);
  const [memProjects, setMemProjects] = useState(projects);
  const [projectTypeFilters, setProjectTypeFilters] = useState(project_type_ids);
  const [clientFilters, setClientFilters] = useState(client_ids);
  const [layoutContentTypeFilters, setLayoutContentTypeFilters] = useState(layout_content_type_ids);
  const firstRender = useRef(true);
  const projectsLeft = memTotal - memProjects.length;
  const loadMoreCount = Math.min(per_page, projectsLeft);

  const loadMore = () => {
    const currentPage = memProjects.length / per_page;
    const queryParams = {
      project_type_ids: projectTypeFilters,
      client_ids: clientFilters,
      layout_content_type_ids: layoutContentTypeFilters,
      page: currentPage + 1,
    };
    const fetchURL = buildURLWithQueryString(sourceURL, queryParams);

    fetch(
      fetchURL,
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    ).then(response =>
      response.json()
    ).then(data => {
      setMemProjects(currentProjects => [...currentProjects, ...data.projects]);
    });
  };

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const queryParams = {
      project_type_ids: projectTypeFilters,
      client_ids: clientFilters,
      layout_content_type_ids: layoutContentTypeFilters,
    };
    const fetchURL = buildURLWithQueryString(sourceURL, queryParams);

    fetch(
      fetchURL,
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    ).then(response =>
      response.json()
    ).then(data => {
      setMemProjects(data.projects);
      setMemTotal(data.total);
    });
  }, [projectTypeFilters, clientFilters, layoutContentTypeFilters]);

  return (
    <>
      <ContactModal isOpen={isContactModalOpen} setIsOpen={setIsContactModalOpen} />
      <ContactButton setIsModalOpen={setIsContactModalOpen} mobile={true} />

      <div className="projects-container">
        <div className="projects-filters-wrapper">
          <div className="projects-filters" style={{ top: headerHeight + 'px' }}>
            <ContactButton setIsModalOpen={setIsContactModalOpen} />

            <ProjectsFilter
              label={t("projects.projectType")}
              options={project_type_options}
              selectedOptions={projectTypeFilters}
              onChange={setProjectTypeFilters}
            />

            <ProjectsFilter
              label={t("projects.client")}
              options={client_options}
              selectedOptions={clientFilters}
              onChange={setClientFilters}
            />

            {!is_mobile && (
              <ProjectsFilter
                label={t("projects.layoutContentType")}
                options={layout_content_type_options}
                selectedOptions={layoutContentTypeFilters}
                onChange={setLayoutContentTypeFilters}
              />
            )}
          </div>
        </div>

        <div className="flex-col full-width gap-18">
          {memProjects.map(project => is_mobile ? (
            <ProjectSimpleCard key={project.id} project={project} />
          ) : (
            <ProjectCard key={project.id} project={project} />
          ))}

          {loadMoreCount > 0 && (
            <div className="load-more-wrapper">
              <Button onClick={loadMore}>{t("projects.loadMore", { count: loadMoreCount, left: projectsLeft })}</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
